/**
 * Ad-suppression state + transition logic for app.disableAds().
 *
 * A { all, video, display } flag object whose transitions are idempotent (applying a flag that is
 * already set is a no-op) and where video + display together imply all. Kept framework-free so the
 * state machine can be unit-tested in isolation; enforcement (the display gate) lives in Slot + the
 * disableAds mixin.
 *
 * NOTE: `video` is tracked but not enforced yet — Concert has no video-ad pipeline, only the
 * Connatix/Reverb player slots, which are intentionally left running for now.
 */

/**
 * @typedef {Object} AdSuppressionState
 * @property {boolean} all     All ad delivery is disabled.
 * @property {boolean} video   Video ad delivery is disabled (tracked only; not enforced yet).
 * @property {boolean} display Display (GAM) ad delivery is disabled.
 */

/**
 * @returns {AdSuppressionState} A fresh state with nothing suppressed.
 */
export function createInitialAdSuppressionState() {
  return { all: false, video: false, display: false };
}

/**
 * Apply a disable request to the suppression state, mutating it in place. `all` sets every flag;
 * `video`/`display` set their own flag, and once both are set `all` becomes true as well.
 *
 * @param {AdSuppressionState} state   The state to update.
 * @param {{all?: boolean, video?: boolean, display?: boolean}} [options={all: true}]
 * @returns {boolean} Whether any flag actually changed — lets the caller fire the event / run the
 *   teardown sweep exactly once and no-op on repeat requests.
 */
export function applyDisable(state, options = { all: true }) {
  let changed = false;

  if (options.all) {
    if (!state.all) {
      state.all = true;
      state.video = true;
      state.display = true;
      changed = true;
    }

    return changed;
  }

  if (options.video && !state.video) {
    state.video = true;
    if (state.display) state.all = true;
    changed = true;
  }

  if (options.display && !state.display) {
    state.display = true;
    if (state.video) state.all = true;
    changed = true;
  }

  return changed;
}

/**
 * Whether the given ad type is currently suppressed.
 *
 * @param {AdSuppressionState} state
 * @param {'all'|'video'|'display'} [type='all']
 * @returns {boolean}
 */
export function isSuppressed(state, type = 'all') {
  return Boolean(state && state[type]);
}
